import React, { useEffect, useState } from 'react';

import ArrowLeftIcon from 'assets/images/icons/arrow-left.svg';

import styles from 'assets/scss/ui/Pagination.module.scss';

interface PaginationProps {
  pages: number;
  currentPage: number;
  onChange?: (page: number) => void;
}

type PageItem = number | 'dots';

const range = (start: number, end: number): number[] =>
  Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);

/**
 * FR-89 п.5: ограничивает количество отображаемых номеров страниц,
 * вставляя "…" при большом их количестве.
 * `siblings` — сколько соседних страниц показывать вокруг текущей.
 */
const getPageItems = (
  current: number,
  total: number,
  siblings: number,
): PageItem[] => {
  // first + last + current + 2 соседних блока + 2 многоточия
  const totalSlots = siblings * 2 + 5;
  if (total <= totalSlots) {
    return range(1, total);
  }

  const leftSibling = Math.max(current - siblings, 1);
  const rightSibling = Math.min(current + siblings, total);
  const showLeftDots = leftSibling > 2;
  const showRightDots = rightSibling < total - 1;

  if (!showLeftDots && showRightDots) {
    return [...range(1, 3 + siblings * 2), 'dots', total];
  }
  if (showLeftDots && !showRightDots) {
    return [1, 'dots', ...range(total - (2 + siblings * 2), total)];
  }
  return [1, 'dots', ...range(leftSibling, rightSibling), 'dots', total];
};

export const Pagination: React.FC<PaginationProps> = ({
  pages,
  currentPage,
  onChange,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mq = window.matchMedia('(max-width: 768px)');
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(event.matches);
    };

    handleChange(mq);

    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  const items = getPageItems(currentPage, pages, isMobile ? 0 : 1);

  const handleClick = (page: number) => {
    if (onChange && page !== currentPage) {
      onChange(page);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) handleClick(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < pages) handleClick(currentPage + 1);
  };

  return (
    <div className={styles.pagination}>
      <button
        type="button"
        onClick={handlePrev}
        className={styles.paginationArrow}
        aria-label="Previous page"
        disabled={currentPage <= 1}
      >
        <img src={ArrowLeftIcon} alt="" className={styles.arrowIcon} />
      </button>

      {items.map((item, index) =>
        item === 'dots' ? (
          <span key={`dots-${index}`} className={styles.pageDots}>
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            className={
              item === currentPage
                ? `${styles.pageButton} ${styles.pageButtonActive}`
                : styles.pageButton
            }
            onClick={() => handleClick(item)}
          >
            {item}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={handleNext}
        className={styles.paginationArrow}
        aria-label="Next page"
        disabled={currentPage >= pages}
      >
        <img
          src={ArrowLeftIcon}
          alt=""
          className={`${styles.arrowIcon} ${styles.arrowIconRight}`}
        />
      </button>
    </div>
  );
};
